/* eslint-disable no-underscore-dangle */

const APP_KEY = 'app/'
const STATUS_KEY = 'status/'
const DETAIL_KEY = 'detail/'

const STATE_KEY = 'state' // inside STATUS
const INFO_KEY = 'info' 
const UPDATED_KEY = 'updated'

/**
 * @class The AppState allows the using application to store its current
 * state in a common way, as well as any app specific data needed.
 * 
 * This is expecting a Database Adapter as its argument. It is assumed
 * that the AppState owns the root of the database, as destroy() will
 * remove it.
 *
 * Structure :
 * {
 *    status : {
 *      state : 'initializing' | 'started' | ...
 *      info : 'sub state'
 *      detail : { } 
 *      updated : timestamp
 *    }
 *    app : { }
 * }
 **/
export default class AppState {
  
  static INITIALIZING = 'initializing'

  static INITIALIZED = 'initialized'

  static STARTED = 'started'

  static PAUSED = 'paused'

  static STOPPED = 'stopped'

  static DESTROYING = 'destroying'

  static DESTROYED = 'destroyed' // unneeded

  constructor( db, name ) {
    this._db = db
    this._access = db.service( name || db.options.appName )
  }

  /**
   * @function destroy Complete removes the state for this app
   * @param {function} onComplete
   **/
  destroy( onComplete ) {
    return this._access.remove( null, onComplete )
  }

  /**
   * @function signIn sign in to the database
   */
  signIn( ) {
    return this._db.signIn( )
  }

  /**
   * @function setState sets the status state
   * @param {any} value value to set the state to
   * @param {function} onComplete 
   */
  setState( value, onComplete ) { 
    const data = { }
    data[STATE_KEY] = value
    data[UPDATED_KEY] = this._access.timestampValue()
    return this._access.update( STATUS_KEY, data, onComplete )
  }

  /**
   * @function setInfo Sets an optional sub-state value into the status block
   * @param {any} value value to set the info to
   * @param {function} onComplete
   **/
  setInfo( value, onComplete ) { 
    const data = { }
    data[INFO_KEY] = value
    data[UPDATED_KEY] = this._access.timestampValue()
    return this._access.update( STATUS_KEY, data, onComplete )
  }

  /**
   * @function getInfo gets info from the database
   * @param {function} onComplete 
   * @param {function} onError
   */
  getInfo( onComplete, onError ) {
    return this._access.once( STATUS_KEY + INFO_KEY, onComplete, onError )
  }
  
  /**
   * @function setDetailData sets the value at the path
   * @param {string} path path to set the value at
   * @param {any} value data to set at the path
   * @param {function} onComplete 
   */
  setDetailData( path, value, onComplete ) {
    if ( path == null ) {
      return null
    }
    const data = { }
    data[DETAIL_KEY + path] = value
    data[UPDATED_KEY] = this._access.timestampValue()

    return this._access.update( STATUS_KEY, data, onComplete )
  }

  /**
   * @function removeDetailData removes the data at the path
   * @param {string} path the path to remove the data at
   * @param {function} onComplete 
   */
  removeDetailData( path, onComplete ) {
    const data = { }
    data[DETAIL_KEY + path] = null
    data[UPDATED_KEY] = this._access.timestampValue()

    return this._access.update( STATUS_KEY, data, onComplete )
  }

  /**
   * @function getDetailData gets the data on the path
   * @param {string} path the path to get the data from
   * @param {function} onComplete 
   * @param {function} onError 
   * @param {any} cxt 
   */
  getDetailData( path, onComplete, onError, cxt ) {
    return this._access.once( STATUS_KEY + DETAIL_KEY + path, onComplete, onError, cxt )
  }

  /**
   * @function getStatus Returns a Promise that is querying the app for its full status.
   * @param {function} onComplete 
   * @param {function} onError
   **/
  getStatus( onComplete, onError ) {
    return this._access.once( STATUS_KEY, onComplete, onError )
  }

  /**
   * @function onStatus Listens for changes to the status object
   * @param {function} onComplete
   * @param {function} onCancel
   **/
  onStatus( onComplete, onCancel ) {
    return this._access.on( STATUS_KEY, onComplete, onCancel )
  }

  /**
   * @function offStatus Listens for changes to the status object
   * @param {function} callback
   **/
  offStatus( callback ) {
    return this._access.off( STATUS_KEY, callback )
  }

  /**
   * @function setAppData sets app data at the path
   * @param {string} path path to set the data at
   * @param {any} value data to be set
   * @param {function} onComplete 
   */
  setAppData( path, value, onComplete ) {
    return this._access.set( APP_KEY + path, value, onComplete )
  }

  /**
   * @function getAppData gets the app data at the path
   * @param {string} path path to get the data at
   * @param {function} onComplete 
   * @param {function} onError 
   */
  getAppData( path, onComplete, onError ) {
    return this._access.once( APP_KEY + path, onComplete, onError )
  }

  /**
   * @function onAppData listen for changes to the app data
   * @param {string} path path to listen to app data at 
   * @param {function} onComplete 
   * @param {function} onCancel 
   */
  onAppData( path, onComplete, onCancel ) {
    return this._access.on( APP_KEY + path, onComplete, onCancel )
  }

  /**
   * @function offAppData listen for changes to the app data
   * @param {string} path path to listen to app data at 
   * @param {function} callback 
   */
  offAppData( path, callback ) {
    return this._access.off( APP_KEY + path, callback )
  }

  /**
   * @function updateAppData updates the app data
   * @param {string} path the path to update the data at
   * @param {any} value value to update the app data to
   * @param {function} onComplete 
   */
  updateAppData( path, value, onComplete ) {
    return this._access.update( APP_KEY + path, value, onComplete )
  }

  /**
   * @function removeAppData removes the app data at the path
   * @param {string} path the path to remove the data at 
   * @param {any} value not used?
   * @param {function} onComplete 
   */
  removeAppData( path, value, onComplete ) {
    return this._access.remove( APP_KEY + path, onComplete )
  }
}
